import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, forkJoin, map, of, startWith, switchMap, takeUntil } from 'rxjs';
import countryData from '@assets/data/countries.json';
import naceData from '@assets/data/nace.json';
import { ToastService } from '@services/common/toast.service';
import { UserAction, UserState, UserStatus } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { CognitoAuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { FeatureService } from '@services/features/feature.service';
import { QuestionnaireAction, QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { FileUploadHandlerEvent, FileUpload, FileUploadErrorEvent } from 'primeng/fileupload';
import { FileError } from '../image-selector/image-selector.component';
import { buildCountryCitiesLUT, City } from 'app/@types/shared/world-cities';
import { UtilsService } from '@services/common/utils.service';
import { Country as CountryType } from 'app/@types/shared/world-cities';
import { NaceCode } from 'app/@types/shared/nace-code';

interface Country {
    code: string;
    name: string;
}

export type FormulaireCallCompletion =
    | 'orgSuccess'
    | 'orgFail'
    | 'questSuccess'
    | 'questFail'
    | 'userSuccess'
    | 'userFail'
    | 'passSuccess'
    | 'passFail'
    | 'entitySuccess'
    | 'entityFail';

@Component({
    selector: 'app-settings-form',
    templateUrl: './settings-form.component.html',
    styleUrls: ['./settings-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class SettingsFormComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$: Observable<UserClaims | undefined>;
    @Select(UserState.getUserStatus) userStatus$: Observable<UserStatus | undefined>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$: Observable<QuestionnaireDto>;
    @Input() isOnboarding: boolean = false;
    @Output() onboardedEvent: EventEmitter<any> = new EventEmitter();

    public settingsForm!: FormGroup;
    public activities: NaceCode[];
    public selectedIndustryActivity?: NaceCode;
    public firstYears: number[];
    public countries: Country[] = [];
    private countryCitiesLUT: Map<string, City[]>;
    public filteredCities: City[] | undefined;

    private userStatus: UserStatus;
    private ngUnsubscribe = new Subject<void>();
    public userClaim: UserClaims;
    public questionnaire: QuestionnaireDto;
    public loading: boolean = false;

    public isAdminForm: boolean = false;
    vatPlaceholder = 'BE0123.456.789';
    constructor(
        private authService: CognitoAuthenticationService,
        private formBuilder: FormBuilder,
        private store: Store,
        private toastService: ToastService,
        private translateService: CustomTranslateService,
        private featureService: FeatureService,
        private readonly utilsService: UtilsService,
    ) {
        this.isAdminForm =
            this.featureService.hasUiFeatures(['organization_admin'], false) || this.featureService.hasSudoFeature();
        this.settingsForm = this.formBuilder.group({
            organizationGroup: this.formBuilder.group({
                companyLogo: new FormControl(''),
                companyName: new FormControl('', Validators.required),
                vatNumber: new FormControl(''),
                country: new FormControl('', Validators.required),
                city: new FormControl('0000000'),
                naceCode: new FormControl('', Validators.required),
                industryCode: new FormControl({ value: '', disabled: true }),
                fte: new FormControl(1, [Validators.required, Validators.min(1)]),
                firstYearMeasured: new FormControl(1, Validators.required),
            }),
            userGroup: this.formBuilder.group({
                lastname: new FormControl('', Validators.required),
                firstname: new FormControl('', Validators.required),
                role: new FormControl(''),
                email: new FormControl({ value: '', disabled: true }, Validators.required),
                phoneNumber: new FormControl(null, CustomValidators.phoneNumberValidator),
            }),
            securityGroup: this.formBuilder.group(
                {
                    oldPassword: new FormControl('', Validators.required),
                    newPassword: new FormControl('', [Validators.required, CustomValidators.passwordValidator]),
                    confirmPassword: new FormControl('', Validators.required),
                },
                {
                    validators: [
                        CustomValidators.mustNotMatch('oldPassword', 'newPassword'),
                        CustomValidators.mustMatch('newPassword', 'confirmPassword'),
                    ],
                },
            ),
        });
    }

    get organizationFormGroup(): FormGroup {
        return this.settingsForm.get('organizationGroup') as FormGroup;
    }

    get userFormGroup(): FormGroup {
        return this.settingsForm.get('userGroup') as FormGroup;
    }

    get securityFormGroup(): FormGroup {
        return this.settingsForm.get('securityGroup') as FormGroup;
    }

    ngOnInit(): void {
        this.userStatus$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status) => {
            this.userStatus = status ?? UserStatus.NONE;
        });

        combineLatest([this.questionnaire$, this.userClaims$, this.translateService.onLangChange.pipe(startWith(null))])
            .pipe(
                takeUntil(this.ngUnsubscribe),
                switchMap(([quest, uc, lang]) => {
                    // Create an array of observables for the translations
                    const translationObservables = naceData.map((item) => {
                        const labelTranslation$ = this.translateService.get(`nace.${item.translationKey}`);
                        const industrySectorLabelTranslation$ = this.translateService.get(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        );

                        // Use forkJoin to combine the two translations for each item
                        return forkJoin([labelTranslation$, industrySectorLabelTranslation$]).pipe(
                            map(([labelTranslation, industrySectorLabelTranslation]) => ({
                                ...item,
                                label: this.utilsService.handleTranslation(labelTranslation),
                                industrySectorLabel:
                                    this.utilsService.handleTranslation(industrySectorLabelTranslation),
                            })),
                        );
                    });

                    // Use forkJoin to wait for all translation observables to complete
                    return forkJoin(translationObservables).pipe(
                        map((translatedActivities) => ({
                            quest,
                            uc,
                            lang,
                            translatedActivities,
                        })),
                    );
                }),
            )
            .subscribe(({ quest, uc, lang, translatedActivities }) => {
                this.activities = translatedActivities;

                this.countryCitiesLUT = buildCountryCitiesLUT();
                this.countries = [];
                this.translateService.get('countries').subscribe((countries) => {
                    Object.entries(countries).forEach(([code, name]) => {
                        this.countries.push({ code: code, name: name });
                    });
                    this.countries.sort((a, b) => a.name.localeCompare(b.name));
                });
                this.firstYears = Array.from({ length: 6 }, (_, index) => new Date().getFullYear() - index);
                const organizationFormGroup = this.settingsForm.get('organizationGroup')!;
                if (uc && uc.organization && quest) {
                    this.organizationFormGroup.enable();
                    this.userClaim = uc;
                    this.questionnaire = quest;

                    //todo move this assignment to another callback or merge
                    [this.selectedIndustryActivity] = this.activities.filter(
                        (code) => code.industrySectorId === uc.organization?.industryCode,
                    );
                    organizationFormGroup.get('companyLogo')!.setValue(uc.organization.avatar);
                    organizationFormGroup.get('companyName')!.setValue(uc.organization.name);
                    organizationFormGroup.get('vatNumber')!.setValue(uc.organization.tva);
                    organizationFormGroup.get('country')!.setValue(uc.organization.country);
                    if (organizationFormGroup.get('country')!.value === '')
                        organizationFormGroup.get('city')?.disable();
                    else {
                        this.filteredCities = this.countryCitiesLUT.get(uc.organization.country!);
                        organizationFormGroup.get('city')?.setValue(parseInt(uc.organization.cityCode ?? '0000000'));
                    }
                    organizationFormGroup.get('naceCode')!.setValue(uc.organization.nace);
                    organizationFormGroup.get('industryCode')!.setValue(uc.organization.industryCode);
                    organizationFormGroup.get('firstYearMeasured')!.setValue(this.questionnaire.firstYearMeasured);
                } else {
                    if (quest !== null)
                        // Getting null is normal so we don't want to disable it...
                        this.organizationFormGroup.disable();
                }
            });

        this.authService.authenticatedUser$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (aU) => {
                const userFormGroup = this.settingsForm.get('userGroup')!;
                if (aU) {
                    let idToken = aU.getSignInUserSession()!.getIdToken();
                    userFormGroup
                        .get('lastname')!
                        .setValue('family_name' in idToken.payload ? idToken.payload['family_name'] : '');
                    userFormGroup
                        .get('firstname')!
                        .setValue('given_name' in idToken.payload ? idToken.payload['given_name'] : '');
                    userFormGroup
                        .get('role')!
                        .setValue('custom:role' in idToken.payload ? idToken.payload['custom:role'] : '');
                    userFormGroup.get('email')!.setValue('email' in idToken.payload ? idToken.payload['email'] : '');
                    let phoneNumber: string | null = null;
                    if ('phone_number' in idToken.payload && idToken.payload['phone_number'] !== '+3200000000000')
                        phoneNumber = idToken.payload['phone_number'];
                    userFormGroup.get('phoneNumber')!.setValue(phoneNumber);
                } else {
                    userFormGroup.disable();
                }
            },
            error: (error) => {},
            complete: () => {},
        });

        this.initializeFormValueSubscriptions();
    }

    private initializeFormValueSubscriptions(): void {
        this.organizationFormGroup.get('naceCode')!.valueChanges.subscribe((selectedNace) => {
            [this.selectedIndustryActivity] = this.activities.filter((code) => code.id === selectedNace);
            if (this.selectedIndustryActivity) {
                this.organizationFormGroup
                    .get('industryCode')!
                    .setValue(this.selectedIndustryActivity.industrySectorId);
            } else {
                this.organizationFormGroup.get('industryCode')!.reset();
            }
        });
    }
    onCountryChange(event) {
        if (event.value && event.value !== '') {
            const country: CountryType | undefined = countryData.find((country) => country.id === event.value);
            const vatNumberControl = this.organizationFormGroup.get('vatNumber');
            vatNumberControl?.setValue('');
            vatNumberControl?.clearValidators();
            vatNumberControl?.markAsPristine();
            vatNumberControl?.setErrors(null);
            this.vatPlaceholder = 'BE0123.456.789';
            if (country) {
                if (country.regexVaT && country.vatErrorMessage && country.placeholderVAT) {
                    this.organizationFormGroup
                        .get('vatNumber')
                        ?.addValidators(
                            CustomValidators.regexValidator(new RegExp(country.regexVaT), country.vatErrorMessage),
                        );
                    this.vatPlaceholder = country.placeholderVAT;
                }
            }
        }
    }
    filterCities() {
        const country = this.organizationFormGroup.get('country')?.value;
        if (country) {
            this.filteredCities = this.countryCitiesLUT.get(country);
            this.organizationFormGroup.get('city')?.enable();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onSubmit() {
        const organizationFormGroup = this.settingsForm.get('organizationGroup')!;
        const userFormGroup = this.settingsForm.get('userGroup')!;
        const securityFormGroup = this.settingsForm.get('securityGroup')!;

        // here are initiated the collection and the subject to handle navigation
        // the signals are added in the array of signals to listen if the condition is met below

        let completionSignals: Subject<FormulaireCallCompletion>[] = [];
        let userInfoUpdateResult: Subject<FormulaireCallCompletion>;
        let organizationUpdateResult: Subject<FormulaireCallCompletion>;
        let questionnaireUpdateResult: Subject<FormulaireCallCompletion>;
        let passUpdateResult: Subject<FormulaireCallCompletion>;
        // this.organizationFormGroup.markAllAsTouched();

        this.loading = true;
        if (
            !this.isAdminForm &&
            this.userClaim.organization &&
            organizationFormGroup.valid &&
            (!organizationFormGroup.pristine || (organizationFormGroup.pristine && this.isOnboarding))
        ) {
            const org = this.userClaim.organization;
            const questionnaire = this.questionnaire;
            org.country = organizationFormGroup.get('country')?.value;
            org.cityCode = organizationFormGroup.get('city')?.value;
            org.name = organizationFormGroup.get('companyName')?.value;
            org.tva = organizationFormGroup.get('vatNumber')?.value;
            org.nace = organizationFormGroup.get('naceCode')?.value;
            org.industryCode = organizationFormGroup.get('industryCode')?.value;
            org.avatar = organizationFormGroup.get('companyLogo')?.value;
            questionnaire.firstYearMeasured = organizationFormGroup.get('firstYearMeasured')?.value;
            org.isOnboarded = true;

            organizationUpdateResult = new Subject<FormulaireCallCompletion>();
            questionnaireUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(organizationUpdateResult, questionnaireUpdateResult);
            this.store.dispatch(new UserAction.UpdateOrganization(org, this.isOnboarding, organizationUpdateResult));
            if (org.uid) {
                this.store.dispatch(
                    new QuestionnaireAction.UpdatePropertyAction(org.uid, questionnaire, questionnaireUpdateResult),
                );
            }

            combineLatest([organizationUpdateResult, questionnaireUpdateResult])
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: (combo) => {
                        if (combo[0] === 'orgSuccess' && combo[1] === 'questSuccess') {
                            this.organizationFormGroup.markAsPristine();
                            this.toastService.showSuccess({
                                details: 'components.settingsForm.companySection.toasts.updateSuccess',
                            });
                        }
                        if (combo[0] === 'orgFail' || combo[1] === 'questFail')
                            this.toastService.showError({
                                details: 'components.settingsForm.companySection.toasts.updateError',
                            });
                    },
                });
        }
        if (userFormGroup.valid && (!userFormGroup.pristine || (userFormGroup.pristine && this.isOnboarding))) {
            userInfoUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(userInfoUpdateResult);
            this.authService
                .updateUserAttributes(
                    userFormGroup.get('firstname')?.value,
                    userFormGroup.get('lastname')?.value,
                    userFormGroup.get('phoneNumber')?.value ?? '+3200000000000',
                    userFormGroup.get('role')?.value,
                )
                .subscribe({
                    //todo : message in page or in toast
                    next: (message) => {
                        userInfoUpdateResult.next('userSuccess');
                        userInfoUpdateResult.complete();
                        userFormGroup.markAsPristine();
                        this.toastService.showSuccess({
                            details: 'components.settingsForm.userSection.toasts.updateSuccess',
                        });
                    },
                    error: (message) => {
                        userInfoUpdateResult.next('userFail');
                        userInfoUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.userSection.toasts.updateError',
                        });
                    },
                });
        }
        if (securityFormGroup.valid && !securityFormGroup.pristine) {
            passUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(passUpdateResult);
            this.authService
                .changePassword(
                    securityFormGroup.get('oldPassword')?.value,
                    securityFormGroup.get('newPassword')?.value,
                )
                .subscribe({
                    //todo : message in page or in toast
                    next: (message) => {
                        securityFormGroup.reset();
                        // securityFormGroup.markAsPristine();
                        passUpdateResult.next('passSuccess');
                        passUpdateResult.complete();
                        this.toastService.showSuccess({
                            details: 'components.settingsForm.securitySection.toasts.updateSuccess',
                        });
                    },
                    error: (message) => {
                        passUpdateResult.next('passFail');
                        passUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.securitySection.toasts.updateError',
                        });
                    },
                });
        }

        /**
         * all the api calls emit a completion signal with a type allowing to know what has success/failed errors and toast can be called before but need to know if everything succeded before navigation.
         * it's also possible to handle success/error from this callback
         *  if (completionSignals.length !== 0) {
         *      this.loading = true;
         *  }
         */
        combineLatest(completionSignals)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (callbacks) => {
                    if (
                        this.isOnboarding &&
                        callbacks.includes('orgSuccess') &&
                        callbacks.includes('userSuccess') &&
                        this.userStatus === UserStatus.ONBOARDED
                    ) {
                        this.loading = false;
                        this.onboardedEvent.emit(true);
                    }

                    this.loading = false;
                },
            });
    }

    // sometimes even if the securityform is not valid because it's empty, it is possible to submit
    public customFormValidity(): boolean {
        return this.isAdminForm
            ? this.userFormGroup.valid &&
                  (this.securityFormGroup.pristine || this.securityFormGroup.valid) &&
                  (!this.userFormGroup.pristine || !this.securityFormGroup.pristine)
            : this.organizationFormGroup.valid &&
                  this.userFormGroup.valid &&
                  (this.securityFormGroup.pristine || this.securityFormGroup.valid) &&
                  (!this.organizationFormGroup.pristine ||
                      !this.userFormGroup.pristine ||
                      !this.securityFormGroup.pristine);
    }
    onLabelClick(id: string) {
        const primeInput = document.getElementById(id);
        if (primeInput) {
            const input = this.getInputElement(primeInput);
            input?.focus();
        }
    }
    getInputElement(primeInput: HTMLElement): HTMLElement | null {
        return (
            primeInput.querySelector('p-dropdown') ||
            primeInput.querySelector('p-radiobutton') ||
            primeInput.querySelector('input')
        );
    }
}
